import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from 'cn';
import { type ReactNode, useEffect, useRef } from 'react';

import { ComponentTestIds } from '~/constants/test-ids';

import { ListItem } from './ListItem';

type P = {
  readonly fetchNextPage: () => void;
  readonly hasDivider: boolean;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly itemHeight: number;
  readonly items: any[];
  readonly renderItem: (item: any) => ReactNode;
  readonly totalCount: number;
} & ComponentStyling;

/**
 * @deprecated Use VirtualizedListServerDriven from @vestigas/ui instead!
 */
export const VirtualizedListServerDriven = ({
  className = 'h-96',
  fetchNextPage,
  hasDivider = true,
  hasNextPage,
  isFetchingNextPage,
  itemHeight = 48,
  items,
  renderItem,
  style,
  totalCount = 0,
}: P) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: totalCount,
    estimateSize: () => itemHeight,
    getScrollElement: () => parentRef.current,
    overscan: 5,
  });

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= items.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    items.length,
    isFetchingNextPage,
    rowVirtualizer.getVirtualItems(),
  ]);

  return (
    <div
      ref={parentRef}
      className={cn('overflow-y-auto', className)}
      style={style}
    >
      <ul
        className={cn('relative snap-y list-none', {
          'divide-y': hasDivider,
        })}
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
      >
        {rowVirtualizer.getVirtualItems().map(({ index, size, start }) => {
          const isLoadingRow = index > items.length - 1;

          return (
            <li
              key={index}
              className="absolute top-0 left-0 w-full border-solid border-gray-200 last:border-b!"
              data-testid={
                ComponentTestIds.MULTI_SELECT.LIST_SERVER_DRIVEN_ITEM_ASSIGNABLE
              }
              style={{
                height: `${size}px`,
                transform: `translateY(${start}px)`,
              }}
            >
              {isLoadingRow ? (
                <ListItem>
                  {hasNextPage
                    ? 'Lade Daten...'
                    : 'Alle verfügbaren Daten geladen'}
                </ListItem>
              ) : (
                renderItem(items[index])
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
